import {
	Id,
	LaravelResponse,
	PaginatedListFetcherConfig,
	SelectedRows,
	keyToUrl,
	withDestroyEndpoint,
	withIndexEndpoint,
	withShowEndpoint,
	withUpdateEndpoint,
} from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { ApplicationIndexResource, ApplicationUpdateRequest, ApplicationUpdateResource, IRole } from "../types";
import {
	CarFleetApplicationCreateRequest,
	DeepApplicationCreateRequest,
	IApplicationCarFleetResource,
	KronosApplicationCreateRequest,
	MailcoachApplicationCreateRequest,
	ParticipantManagementApplicationCreateRequest,
	ParticipantManagementUpdateRequest,
} from "../types/application";

const emptyRoles: IRole[] = [];

export const useApplicationRolesQuery = (applicationId: Id) => {
	const { data, ...rest } = useQuery({
		queryKey: applicationService.keys.roles(applicationId),
		queryFn: () => applicationService.getRoles(applicationId).then((r) => r.data.data),
	});

	return {
		...rest,
		data: data ? data : emptyRoles,
	};
};

const emptyMailcoachApplications: ApplicationIndexResource[] = [];

export const useMailcoachApplicationsQuery = () => {
	const { data, ...rest } = useQuery({
		queryKey: applicationService.keys.allMailcoach,
		queryFn: () => applicationService.allMailcoach().then((r) => r.data.data),
	});

	return {
		...rest,
		data: data ? data : emptyMailcoachApplications,
	};
};

export const createUserApplicationService = (id: Id) => {
	const baseKey = ["users", `${id}`, "applications"];

	return {
		...withIndexEndpoint<ApplicationIndexResource>(baseKey),
		...withShowEndpoint<ApplicationUpdateResource>(baseKey),
		detachApplications: (applicationId: Id) =>
			axios.post<LaravelResponse<void>>(`${keyToUrl([...baseKey, `${applicationId}`, "detach"])}`),
	};
};

const baseKey = "applications";

const keys = {
	index: [baseKey],
	list: [baseKey, "list"],
	allCarFleet: [baseKey, "car-fleet"],
	allMailcoach: [baseKey, "mailcoach"],
	storeParticipantManagement: [baseKey, "participant-management"],
	storeDeep: [baseKey, "deep"],
	storeKronos: [baseKey, "kronos"],
	storeCarFleet: [baseKey, "car-fleet"],
	storeMailcoach: [baseKey, "mailcoach"],
	details: (id: Id) => [baseKey, `${id}`],
	updateParticipantManagement: (id: Id) => [baseKey, `${id}`, "participant-management"],
	roles: (id: Id) => [baseKey, `${id}`, "roles"],
	updateRoles: (id: Id) => [baseKey, `${id}`, "roles", "update"],
};

const urls = {
	index: keyToUrl(keys.index),
	allCarFleet: keyToUrl(keys.allCarFleet),
	allMailcoach: keyToUrl(keys.allMailcoach),
	details: (id: Id) => keyToUrl(keys.details(id)),
	roles: (id: Id) => keyToUrl(keys.roles(id)),
	updateRoles: (id: Id) => keyToUrl(keys.updateRoles(id)),
	updateParticipantManagement: (id: Id) => keyToUrl(keys.updateParticipantManagement(id)),
	storeParticipantManagement: keyToUrl(keys.storeParticipantManagement),
	storeDeep: keyToUrl(keys.storeDeep),
	storeKronos: keyToUrl(keys.storeKronos),
	storeCarFleet: keyToUrl(keys.storeCarFleet),
	storeMailcoach: keyToUrl(keys.storeMailcoach),
};

export const applicationService = {
	keys,
	index: (
		{ pageParams, sortParams, searchParams }: PaginatedListFetcherConfig<ApplicationIndexResource>,
		activeFilterId: Id | null,
	) => {
		const params = { ...pageParams, ...sortParams, ...searchParams, filterId: activeFilterId };
		return axios.get<LaravelResponse<ApplicationIndexResource[]>>(urls.index, { params });
	},
	allCarFleet: () => axios.get<LaravelResponse<IApplicationCarFleetResource[]>>(urls.allCarFleet),
	allMailcoach: () => axios.get<LaravelResponse<ApplicationIndexResource[]>>(urls.allMailcoach),
	getRoles: (applicationId: Id) => axios.get<LaravelResponse<IRole[]>>(urls.roles(applicationId)),
	updateRoles: (applicationId: Id, roleIds: Id[], userIds: SelectedRows) =>
		axios.post(urls.updateRoles(applicationId), { roleIds, userIds }),
	storeParticipantManagement: (values: ParticipantManagementApplicationCreateRequest) =>
		axios.post<LaravelResponse<Id>>(urls.storeParticipantManagement, values),
	storeDeep: (values: DeepApplicationCreateRequest) => axios.post<LaravelResponse<Id>>(urls.storeDeep, values),
	storeKronos: (values: KronosApplicationCreateRequest) => axios.post<LaravelResponse<Id>>(urls.storeKronos, values),
	storeCarFleet: (values: CarFleetApplicationCreateRequest) =>
		axios.post<LaravelResponse<Id>>(urls.storeCarFleet, values),
	storeMailcoach: (values: MailcoachApplicationCreateRequest) =>
		axios.post<LaravelResponse<Id>>(urls.storeMailcoach, values),
	updateParticipantManagement: (id: Id, values: ParticipantManagementUpdateRequest) =>
		axios.put<LaravelResponse<void>>(urls.updateParticipantManagement(id), values),
	...withDestroyEndpoint([baseKey]),
	...withUpdateEndpoint<ApplicationUpdateRequest, ApplicationUpdateResource>([baseKey]),
	...withShowEndpoint<ApplicationUpdateResource>([baseKey]),
};
